import React from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, ShieldAlert, ShieldCheck, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { sdk } from '@/lib/sdk'
import { SUPPORT_SESSION_FORBIDDEN_EVENT, setStepUpHandler } from '@/lib/twoFactor/stepUpBridge'
import type { TwoFactorCodeSent } from '@/lib/sdk-official/resources/two-factor'
import { TwoFactorCodeForm, type CodeDelivery } from './TwoFactorCodeForm'
import { twoFactorFailure } from '@/lib/twoFactor/failure'

type Answer = { token: string; expiresAt: string } | null

/**
 * F-233: the step-up modal, mounted once. When a protected action (turning
 * 2FA off, its phone, new recovery codes, an API key, a payout, the payout
 * account) answers 403 `STEP_UP_REQUIRED`, the transport asks here: a code
 * goes to the 2FA phone, the person types it (or a recovery code), and the
 * action is sent again with the token. Closing gives the action up.
 *
 * In the SuperAdmin's support session those actions answer
 * `SUPPORT_SESSION_FORBIDDEN` instead: no modal, a notice says why.
 */
export const StepUpHost = () => {
  const { t } = useTranslation('twoFactor')
  const [open, setOpen] = React.useState(false)
  const [delivery, setDelivery] = React.useState<CodeDelivery | null>(null)
  const [sending, setSending] = React.useState(false)
  const [notice, setNotice] = React.useState<{ text: string; suggestRecovery: boolean } | null>(null)
  const [supportNotice, setSupportNotice] = React.useState<string | null>(null)
  const resolveRef = React.useRef<((answer: Answer) => void) | null>(null)

  const finish = React.useCallback((answer: Answer) => {
    resolveRef.current?.(answer)
    resolveRef.current = null
    setOpen(false)
  }, [])

  const sendCode = React.useCallback(async (channel?: 'whatsapp' | 'sms'): Promise<TwoFactorCodeSent> => {
    const sent = await sdk.twoFactor.requestStepUp(channel)
    setDelivery({ phone: sent.phone, sentVia: sent.sentVia, resendAfterSeconds: sent.resendAfterSeconds })
    return sent
  }, [])

  React.useEffect(() => {
    setStepUpHandler(() => new Promise<Answer>((resolve) => {
      resolveRef.current = resolve
      setDelivery(null)
      setNotice(null)
      setOpen(true)
      setSending(true)
      sendCode()
        .catch((err) => setNotice(twoFactorFailure(err, t)))
        .finally(() => setSending(false))
    }))
    return () => setStepUpHandler(null)
  }, [sendCode, t])

  React.useEffect(() => {
    let timer: number | undefined
    const onRefused = (event: Event) => {
      setSupportNotice((event as CustomEvent<string>).detail || t('stepUp.supportSession'))
      window.clearTimeout(timer)
      timer = window.setTimeout(() => setSupportNotice(null), 10_000)
    }
    window.addEventListener(SUPPORT_SESSION_FORBIDDEN_EVENT, onRefused)
    return () => {
      window.removeEventListener(SUPPORT_SESSION_FORBIDDEN_EVENT, onRefused)
      window.clearTimeout(timer)
    }
  }, [t])

  const verify = async (answer: { code?: string; recoveryCode?: string }) => {
    const result = await sdk.twoFactor.verifyStepUp(answer)
    finish({ token: result.stepUpToken, expiresAt: result.expiresAt })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => { if (!next) finish(null) }}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto p-4 sm:p-6" data-testid="step-up-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" />{t('stepUp.title')}</DialogTitle>
            <DialogDescription>{t('stepUp.description')}</DialogDescription>
          </DialogHeader>
          <div className="min-w-0">
            {sending && !delivery ? (
              <p className="flex items-center gap-2 py-6 text-sm text-foreground-secondary"><Loader2 className="h-4 w-4 animate-spin" />{t('stepUp.sending')}</p>
            ) : (
              <TwoFactorCodeForm
                delivery={delivery}
                notice={notice}
                onSubmit={verify}
                onResend={sendCode}
                submitLabel={t('stepUp.submit')}
                testId="step-up-form"
                secondary={<Button type="button" variant="outline" className="w-full sm:w-auto" onClick={() => finish(null)}>{t('stepUp.cancel')}</Button>}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      {supportNotice && (
        <div role="alert" data-testid="support-session-notice"
          className="fixed bottom-20 left-1/2 z-[60] flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 items-start gap-2 rounded-lg border border-warning-text/20 bg-warning-surface px-4 py-3 text-sm text-warning-text shadow-lg lg:bottom-6">
          <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span className="min-w-0 flex-1">{supportNotice}</span>
          <button type="button" onClick={() => setSupportNotice(null)} aria-label={t('stepUp.dismiss')} className="flex-shrink-0"><X className="h-4 w-4" /></button>
        </div>
      )}
    </>
  )
}
